import * as React from "react"
import { cn } from "cn"

import { Hint } from "@/components/analytics/hint"
import { Table, TableCaption } from "@/components/ui/table"

// Shared visual vocabulary for the report lenses.
//
// Everything here exists because the lenses used to be number soup: twelve
// right-aligned columns with no encoding, so nothing could be scanned. These
// primitives add the missing visual channel without adding a chart library on
// top of recharts, and they keep one rule per colour job:
//
//   ORDINAL  (--step-1..5)  ordered buckets — funnel stages, aging, age bands.
//                           step-1 is always "least"; the theme flips the
//                           anchor in dark mode, so callers never branch.
//   DIVERGING(--gain/--loss) money moving the right or wrong way.
//   STATUS   (health tones) reserved for health / sentiment state only.
//
// Nominal categories (reps, cities, task types) are ONE hue — bar length
// already encodes the value, so spending the identity channel on it would say
// nothing twice.

/** The five ordinal steps, least → most. */
export const STEP_BG = ["bg-step-1", "bg-step-2", "bg-step-3", "bg-step-4", "bg-step-5"] as const
export const STEP_VAR = [
  "var(--step-1)",
  "var(--step-2)",
  "var(--step-3)",
  "var(--step-4)",
  "var(--step-5)",
] as const

// --- Layout -------------------------------------------------------------------

/**
 * A row of cards that are all the same height.
 *
 * `grid` with a fixed `auto-rows` and the default `items-stretch` is what makes
 * the heights equal: every child is sized by the row, so a short list card and
 * a tall chart card can no longer disagree. Cards clip their own overflow, and
 * the scrollable regions below (`ScrollBody`) keep long content reachable.
 */
export function CardRow({
  children,
  size = "chart",
  className,
}: {
  children: React.ReactNode
  /**
   * chart = 22.5rem, which is exactly `h-90` (90 × the 0.25rem spacing step) —
   * keep it in step with the chart cards that set their own `h-90`, or a row
   * mixing the two goes back to being uneven. tall = 27rem.
   */
  size?: "chart" | "tall"
  className?: string
}) {
  return (
    <div
      className={cn(
        "grid grid-cols-1 gap-4 lg:grid-cols-2",
        size === "tall" ? "lg:auto-rows-[27rem]" : "lg:auto-rows-[22.5rem]",
        className,
      )}
    >
      {children}
    </div>
  )
}

// --- Phones -------------------------------------------------------------------
//
// Class sets for the report cards below the `lg` breakpoint. Desktop layout is
// untouched: every class here is behind a `max-*` variant.

/**
 * On a CardHeader that carries a CardAction: below `lg` (phones, tablets and
 * landscape phones, where the content column is under ~700px) the action drops
 * under the title instead of squeezing the title and description into a
 * narrow column beside it. Pair with `PHONE_ACTION`.
 */
export const PHONE_HEADER = "max-lg:has-data-[slot=card-action]:grid-cols-1"

/** On the CardAction itself — full row, scrolls sideways when the toggles don't fit. */
export const PHONE_ACTION =
  "max-lg:col-start-1 max-lg:row-span-1 max-lg:row-start-auto max-lg:mt-1.5 max-lg:max-w-full max-lg:justify-self-start max-lg:overflow-x-auto max-lg:[scrollbar-width:none]"

/** First column of a wide table: pinned while the numbers scroll under it, below `lg`. */
export const STICKY_COL = "max-lg:sticky max-lg:left-0 max-lg:z-10 max-lg:bg-card"

/**
 * A wide lens table that scrolls sideways inside its card. Below `lg` the note
 * moves out from under the table — as a caption it is as wide as the scrolled
 * table, so most of it sits off-screen — and wraps to the card instead.
 */
export function WideTable({ note, children }: { note: React.ReactNode; children: React.ReactNode }) {
  return (
    <>
      <div className="overflow-x-auto">
        <Table>
          {children}
          <TableCaption className="max-lg:hidden">{note}</TableCaption>
        </Table>
      </div>
      <p className="text-sm text-muted-foreground lg:hidden">{note}</p>
    </>
  )
}

/** Fills the rest of a fixed-height card and scrolls rather than clipping. */
export function ScrollBody({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn("-mx-1 min-h-0 flex-1 overflow-y-auto px-1", className)}>{children}</div>
  )
}

// --- Numbers ------------------------------------------------------------------

/**
 * One headline number. Proportional figures on purpose: tabular digits make a
 * standalone value at this size read loose, and nothing aligns vertically here.
 */
export function StatTile({
  label,
  value,
  hint,
  tone,
  meter,
}: {
  label: string
  value: React.ReactNode
  hint?: React.ReactNode
  /** Text colour for the value — status tones only (text-success / -warning / -destructive). */
  tone?: string
  /** 0-100; draws a meter under the value. */
  meter?: { pct: number; tone?: string } | null
}) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <p className="truncate text-xs font-medium tracking-wide text-muted-foreground uppercase">
        {label}
      </p>
      <p className={cn("truncate text-2xl font-semibold", tone)}>{value}</p>
      {meter ? <Meter pct={meter.pct} tone={meter.tone} /> : null}
      {hint ? (
        <p className="truncate text-xs text-muted-foreground max-sm:line-clamp-2 max-sm:whitespace-normal">
          {hint}
        </p>
      ) : null}
    </div>
  )
}

/** A single ratio against its limit. Not a two-slice pie. */
export function Meter({
  pct,
  tone = "bg-step-3",
  className,
}: {
  pct: number
  tone?: string
  className?: string
}) {
  const w = Math.max(0, Math.min(100, pct))
  return (
    <div className={cn("h-1.5 w-full overflow-hidden rounded-full bg-muted", className)}>
      <div className={cn("h-full rounded-full", tone)} style={{ width: `${w}%` }} />
    </div>
  )
}

/**
 * A table cell that shows magnitude as well as the number. The bar is anchored
 * to the cell's right edge so it shares a baseline with the right-aligned
 * figure above it.
 */
export function BarCell({
  text,
  value,
  max,
  tone = "bg-step-3",
}: {
  text: React.ReactNode
  value: number
  max: number
  tone?: string
}) {
  const pct = max > 0 ? Math.max(value > 0 ? 4 : 0, (value / max) * 100) : 0
  return (
    <div className="flex min-w-14 flex-col items-end gap-1.5">
      <span className="tabular-nums">{text}</span>
      <span className="h-1 w-full overflow-hidden rounded-full bg-muted-foreground/20">
        <span className={cn("ml-auto block h-full rounded-full", tone)} style={{ width: `${pct}%` }} />
      </span>
    </div>
  )
}

// --- Part to whole ------------------------------------------------------------

export type Segment = {
  key: string
  label: string
  value: number
  /** Background class for the fill. */
  tone: string
  /** Shown instead of the raw value in the hover label. */
  text?: string
}

/**
 * Part-to-whole in one bar: a 2px surface gap separates the fills, never a
 * border. Values are never written inside the fills — the ordinal ramp flips
 * anchor between light and dark, so there is no one ink colour that stays
 * legible on every step. The legend carries the numbers instead.
 */
export function SegmentBar({
  segments,
  size = "thin",
  className,
  emptyLabel = "No data",
}: {
  segments: readonly Segment[]
  /** thin = a 10px strip that fits a table cell; bold = a 14px bar for a card. */
  size?: "thin" | "bold"
  className?: string
  emptyLabel?: string
}) {
  const parts = segments.filter((s) => s.value > 0)
  const total = parts.reduce((s, p) => s + p.value, 0)
  const bold = size === "bold"
  if (total === 0) {
    return (
      <div
        className={cn("rounded-full bg-muted", bold ? "h-3.5 w-full" : "h-2.5 w-20", className)}
        aria-label={emptyLabel}
      />
    )
  }
  return (
    <div
      className={cn("flex gap-0.5", bold ? "h-3.5 w-full" : "h-2.5 w-20", className)}
      role="img"
      aria-label={parts.map((p) => `${p.label} ${p.text ?? p.value}`).join(", ")}
    >
      {parts.map((p) => {
        const share = (p.value / total) * 100
        return (
          <Hint
            key={p.key}
            label={`${p.label} · ${p.text ?? p.value.toLocaleString("en-US")} (${Math.round(share)}%)`}
            render={
              <div
                className={cn("min-w-0 rounded-sm", p.tone)}
                style={{ width: `${share}%` }}
              />
            }
          />
        )
      })}
    </div>
  )
}

/** Swatch + name + count. Present whenever two or more series share a bar. */
export function Legend({
  items,
  className,
}: {
  items: readonly { key: string; label: string; tone: string; text?: React.ReactNode }[]
  className?: string
}) {
  return (
    <ul className={cn("flex flex-wrap gap-x-4 gap-y-1 text-xs", className)}>
      {items.map((i) => (
        <li key={i.key} className="flex items-center gap-1.5">
          <span className={cn("size-2.5 shrink-0 rounded-full ring-1 ring-foreground/15", i.tone)} />
          <span className="text-muted-foreground">{i.label}</span>
          {i.text !== undefined && <span className="font-medium tabular-nums">{i.text}</span>}
        </li>
      ))}
    </ul>
  )
}

// --- Ranked magnitude ---------------------------------------------------------

export type HBarRow = {
  key: string
  label: string
  value: number
  /** Formatted value shown at the bar's end. */
  text?: React.ReactNode
  /** Secondary value drawn as a tick on the same scale, e.g. a p90 against a median. */
  marker?: { value: number; label: string } | null
  caption?: React.ReactNode
}

/**
 * Compare magnitude across nominal categories: one hue, sorted long-to-short,
 * value direct-labelled at the bar end. A bar chart rather than recharts
 * because there is no axis worth the weight at this size — the direct labels
 * carry the numbers.
 */
export function HBars({
  rows,
  tone = "bg-step-3",
  emptyMessage = "Nothing to compare yet.",
  max: maxOverride,
  className,
}: {
  rows: readonly HBarRow[]
  tone?: string
  emptyMessage?: string
  /** Share a scale with a sibling chart instead of self-scaling. */
  max?: number
  className?: string
}) {
  const max = maxOverride ?? Math.max(1, ...rows.map((r) => Math.max(r.value, r.marker?.value ?? 0)))
  if (rows.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">{emptyMessage}</p>
  }
  return (
    <ul className={cn("flex flex-col gap-2.5", className)}>
      {rows.map((r) => {
        const pct = Math.max(r.value > 0 ? 1.5 : 0, (r.value / max) * 100)
        return (
          <li key={r.key} className="grid grid-cols-[minmax(0,7rem)_1fr] items-center gap-2 sm:grid-cols-[minmax(0,10rem)_1fr] sm:gap-3">
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-sm" title={r.label}>
                {r.label}
              </span>
              {r.caption ? (
                <span className="truncate text-[11px] text-muted-foreground">{r.caption}</span>
              ) : null}
            </div>
            <div className="flex min-w-0 items-center gap-2">
              <div className="relative h-2.5 min-w-0 flex-1 rounded-full bg-muted/70">
                <div
                  className={cn("h-full rounded-full rounded-l-sm", tone)}
                  style={{ width: `${pct}%` }}
                />
                {r.marker && r.marker.value > 0 && (
                  <Hint
                    label={r.marker.label}
                    render={
                      <span
                        className="absolute inset-y-[-3px] w-0.5 rounded-full bg-foreground/55"
                        style={{
                          left: `calc(${Math.min(100, (r.marker.value / max) * 100)}% - 1px)`,
                        }}
                      />
                    }
                  />
                )}
              </div>
              <span className="w-16 shrink-0 text-right text-xs text-muted-foreground tabular-nums">
                {r.text ?? r.value.toLocaleString("en-US")}
              </span>
            </div>
          </li>
        )
      })}
    </ul>
  )
}

// --- Journey ------------------------------------------------------------------

/**
 * Where the time goes between stages: segment width is each hop's median, so a
 * long hop looks long. Ordered hops, so the ordinal ramp.
 */
export function JourneyStrip({
  hops,
  totalLabel,
  totalText,
}: {
  hops: readonly { key: string; label: string; days: number | null; samples: number; text: string }[]
  totalLabel: string
  totalText: string
}) {
  const sum = hops.reduce((s, h) => s + Math.max(0, h.days ?? 0), 0)
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
          {totalLabel}
        </span>
        <span className="text-sm font-semibold tabular-nums">{totalText}</span>
      </div>
      {sum === 0 ? (
        <div className="h-3.5 w-full rounded-full bg-muted" aria-label="No transitions recorded yet" />
      ) : (
        <SegmentBar
          size="bold"
          segments={hops.map((h, i) => ({
            key: h.key,
            label: h.label,
            value: h.days ?? 0,
            text: `${h.text} · median of ${h.samples} transition${h.samples === 1 ? "" : "s"}`,
            tone: STEP_BG[Math.min(i, STEP_BG.length - 1)],
          }))}
        />
      )}
      <Legend
        items={hops.map((h, i) => ({
          key: h.key,
          label: h.label,
          tone: STEP_BG[Math.min(i, STEP_BG.length - 1)],
          text: h.text,
        }))}
      />
    </div>
  )
}

/** Centered empty state sized for a card body. */
export function VizEmpty({ children }: { children: React.ReactNode }) {
  return (
    <p className="flex min-h-24 flex-1 items-center justify-center rounded-lg bg-muted/40 px-4 py-6 text-center text-sm text-muted-foreground">
      {children}
    </p>
  )
}
